import {
  Button,
  Flex,
  Loader,
  Menu,
  ProviderConsumer,
  Text
} from '@fluentui/react-northstar';
import { Communication, TToolbarInteraction } from '@fluentui/react-teams';
import { Providers } from '@microsoft/mgt-element/dist/es6/providers/Providers';
import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { appState, useSetState } from '../AppState';
import { MobileSettingsView } from '../automation/AutomationTable';
import ConfigCSS from '../config/config.module.css';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { Breadcrumbs } from '../shared/components/BreadcrumbNavigation';
import { CheckLogin } from '../shared/components/CheckLogin';
import { Toolbar } from '../shared/components/Toolbar';
import { UpgradeNeeded } from '../shared/components/UpgradeNeeded';
import useUnsavedChangesWarning from '../shared/hooks/useUnsavedChangesWarning';
import { TicketTemplate } from '../shared/interfaces/ticket.interface';
import { graphService } from '../shared/services/graph.service';
import { platformService } from '../shared/services/platform.service';
import {
  AutomationOperators,
  CONTACT_ADMIN,
  DEFAULT_API_STATUS,
  DONE,
  ERROR,
  FILTERS_STRINGS,
  GETTING_TEMPLATE_DATA,
  LICENSE_FEATURE,
  LOADING,
  NEW_TEMPLATE,
  REFRESH_SESSION,
  SAVE,
  SAVING_TEMPLATE_DATA,
  SETTINGS,
  SOMETHING_WENT_WRONG,
  STATUS_GUID,
  TEMPLATE,
  TEMPLATES,
  UNAUTHORIZED_ACCESS
} from '../shared/utils/constants';
import { toTitleCase } from '../shared/utils/helper';
import { loadFilters } from '../tikit/ticketHelper';
import { CardPeoplePicker } from './CustomComponents/PeoplePicker/CardPeoplePicker';
import { Step_DefineFields } from './Step_DefineFields';
import { TemplateFormStateProps } from './TemplateFieldsForm';
import { TemplateForm } from './TemplateForm';
import { CheckFeature } from '../shared/components/CheckFeature';
import { ScopePermissionPicker } from '../Rbac/ScopePermissionPicker';
import { haveScopedMembersChanged } from '../Rbac/AvailableTo/scopedMembers';
import { buildTemplateTabKeys, TemplateTabKey } from './templateTabs';

interface ITicketTemplateProperties {
  id: string;
  menu?: string;
}

export interface ITemplateFormProperties {
  headerContent?: JSX.Element;
  templateData: TicketTemplate;
  dispatchData?: any;
  formData?: TemplateFormStateProps;
  isSelectionView?: boolean;
  defaultValues?: any;
  reRender?: boolean;
}

export const getUpdatedData = (apiData: TicketTemplate) => {
  if (apiData.AssigneeId == -1) {
    delete apiData.AssigneeId;
    apiData.UnsetAssignee = true;
  }
  if (apiData.SupportGroupId == -1) {
    delete apiData.SupportGroupId;
    apiData.UnsetSupportGroup = true;
  }
  if (apiData.TeamId == -1) {
    delete apiData.TeamId;
    apiData.UnsetTeam = true;
  }
  if (apiData.CategoryId == -1) {
    delete apiData.CategoryId;
    apiData.UnsetCategory = true;
  }
  if (apiData.TicketTypeId == -1) {
    delete apiData.TicketTypeId;
    apiData.UnsetTicketType = true;
  }
  const removeProps = [
    'isCardValid',
    'AssigneeName',
    'CategoryName',
    'TicketTypeName',
    'PriorityName',
    'SupportGroupName',
    'TeamName',
    'StatusName',
    'hidden',
    'errorFieldsArray',
    'errorFormsArray',
    'validateFields',
    'validateForms',
    'Category',
    'Status',
    'TicketType',
    'Priority',
    'TemplateForms'
  ];
  removeProps.forEach(prop => {
    delete apiData[prop];
  });

  return apiData;
};

export const checkAndUndefaultOther = async (api: any, currentDefaultId: number) => {
  try {
    const {
      data: { value }
    } = await api.getTicketTemplates(`?$filter=IsDefault%20eq%20true&v=${new Date().getTime()}&skipScoping=true`);

    const defaultList: TicketTemplate[] = value
      .filter(
        (val: TicketTemplate) =>
          val.IsDefault && val.Id !== currentDefaultId
      )

    if (defaultList.length > 0) {
      for (const template1 of defaultList) {
        template1.IsDefault = false;
        await api.updateTicketTemplate(template1);
      }
    }
  } catch (e: any) {
    console.error(e);
  }
  // no finally block to avoid the flicker effect because finally sets the status to done
};

export const TicketTemplates = (props: ITicketTemplateProperties) => {

  const { t } = useTranslation();
  const api = new platformService();
  const currentState = appState();
  const history = useHistory();
  const setState = useSetState();
  const graphAPI = new graphService();
  const [errCode, setErrCode] = useState(0);
  const [isValidating, setIsValidating] = useState(false);
  const [apiStatus, setApiStatus] = useState<APIStatus>(DEFAULT_API_STATUS as APIStatus);
  const defaultData = { errorFieldsArray: [], Name: '', Description: '', CardResponseJson: '', TeamId: -1, SupportGroupId: -1, AssigneeId: -1, TemplateForms: [], NoCustomForm: true, } as TicketTemplate;
  const [data, dispatchData] = React.useReducer(
    (state: TicketTemplate, newState) => {
      state = newState;
      return { ...state, ...newState };
    },
    defaultData
  );
  const [originalData, setOriginalData] = useState(defaultData);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [menu, setMenu] = useState<number>(
    props.menu && props.menu.length > 0 ? parseInt(props.menu) : 0
  );
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [isTemplateFeatureEnabled, setIsTemplateFeatureEnabled] = useState<boolean>(false);
  const [isCustomFormFeatureEnabled, setIsCustomFormFeatureEnabled] = useState<boolean>(false);
  const [isMultiTurnCardFeatureEnabled, setIsMultiTurnCardFeatureEnabled] = useState<boolean>(false);
  const [isScopingEnabled, setScopingEnabled] = useState<boolean>(false);
  const [templateProps, setTemplateProps] = useState<TemplateFormStateProps>(
    {} as TemplateFormStateProps
  );

  const [isFormUpdated, setIsFormUpdated] = useState<boolean>(false);
  const [isFormLogicInvalid, setIsFormLogicInvalid] = useState<boolean>(false);
  const [Prompt, setDirty] = useUnsavedChangesWarning();
  const setFormDirty: (boolVlaue: boolean) => void = setDirty as (boolVlaue: boolean) => void;

  const onToolbarInteraction = (interaction: TToolbarInteraction) => {
    if (interaction.action === 'toggle-menu') {
      setMenuOpen(!menuOpen);
    }
  };

  const fetchData = useCallback(async () => {
    setApiStatus({ ...apiStatus, msg: GETTING_TEMPLATE_DATA });

    try {
      let templateData: any;
      if (props.id === '0') {
        const { data: value } = await api.getDefaultTemplateInstance();
        templateData = JSON.parse(value.value);
        templateData.NoCustomForm = true;
      }else{
        const {data: result} = await api.getTicketTemplates( `(${props.id})?$expand=Status,Category,Priority,TicketType,Tags,AffectedUsers,Collaborators,ScopedMembers,TemplateForms($expand=Form,Transitions($expand=TransitionGroups($expand=Filters)))&skipScoping=true`);
        templateData = result;
      }

      let tags = JSON.parse(JSON.stringify(templateData.Tags));
      templateData.isCardValid = true;
      templateData.errorFieldsArray = [];
      if (templateData['TeamId'] || templateData['SupportGroupId'] || templateData['AssigneeId'] ||
        templateData.UnsetAssignee || templateData.UnsetTeam || templateData.UnsetSupportGroup) {
        if (!templateData['TeamId']) templateData.UnsetTeam = true;
        if (!templateData['SupportGroupId']) templateData.UnsetSupportGroup = true;
        if (!templateData['AssigneeId']) templateData.UnsetAssignee = true;
      }

      const sortedTemplateForms = [...templateData.TemplateForms].sort((a, b) => a.Order - b.Order);
      templateData.TemplateForms = sortedTemplateForms.map(s => {
        s.Name = s.Form?.Name ?? s.Name;
        return s;
      });
      dispatchData(templateData);
      setOriginalData(cloneDeep(templateData));
      setIsDataLoaded(true);

      const results = await Promise.all([
        api.getLifecyclesSafe(`${FILTERS_STRINGS.FILTERS_URL_PARAMS}&$select=Id,Title`),
        (currentState?.ticketStatus?.length > 0) ? Promise.resolve(currentState) : loadFilters(api, graphAPI, currentState, setState),
        getCachedFeature(LICENSE_FEATURE.CustomForms),
        getCachedFeature(LICENSE_FEATURE.TicketTemplates),
        getCachedFeature(LICENSE_FEATURE.MultiTurnCard),
        getCachedFeature(LICENSE_FEATURE.PermissionScoping)
      ]);
      let filterResults = results[1];
      const lifecycles = results[0];
      filterResults["lifecycles"] = lifecycles;

      filterResults.tags.forEach((t: any) => {
        t.selected = tags.some(x => x.Id == t.Id);
      });
      setIsCustomFormFeatureEnabled(results[2]);
      setIsTemplateFeatureEnabled(results[3]);
      setIsMultiTurnCardFeatureEnabled(results[4]);
      setScopingEnabled(results[5]);
      setTemplateProps(filterResults as TemplateFormStateProps);
    } catch (err) {
      setApiStatus({
        ...apiStatus,
        status: ERROR,
        errCode: err['response']?.status
      });
    } finally {
      setApiStatus({ ...apiStatus, status: DONE });
    }
  }, []);

  const loadPage = () => {
    fetchData();
  };

  const errConfig = {
    fields: {
      title: errCode == 401 ? UNAUTHORIZED_ACCESS : SOMETHING_WENT_WRONG,
      desc: CONTACT_ADMIN
    }
  };

  const prepCustomPropsData = (thisData: any) => {
    const newObjData = {};
    currentState?.customProperties.forEach(property => {
      if (thisData[property.PropertyId])
        newObjData[property.PropertyId] = thisData[property.PropertyId];
      delete thisData[property.PropertyId];
    });
    thisData["CustomPropertiesAnswerJson"] = JSON.stringify(newObjData);

    return thisData;
  }

  const saveDataAsync = async () => {
    setApiStatus({ ...apiStatus, msg: SAVING_TEMPLATE_DATA, status: LOADING });

    try {
      const templateFormData = data.TemplateForms;
      let apiData = getUpdatedData(data);
      apiData = prepCustomPropsData(apiData);

      const resultData = await api.updateTicketTemplate(apiData);
      const templateId = resultData.data.Id;
      await api.saveTemplateForms(templateId, templateFormData);


    } catch (err: any) {
      setApiStatus({
        ...apiStatus,
        status: ERROR,
        errCode: err.response.status
      });
    } finally {
      setApiStatus({ ...apiStatus, status: DONE });
      history.push(`/settings/templates`);
    }
  };

  const isFieldsValid = menu => {

    if (data.NoCustomForm) menu = 0;

    if (menuItems[menu]?.key == 'fields') {
      if (!data?.Name)
        data.errorFieldsArray.push('Name');
      if (data?.LifecycleId && data.Status) {
        if (data.Status.Guid === STATUS_GUID.RESOLVED || data.Status.Guid === STATUS_GUID.CLOSED)
          data.errorFieldsArray.push("LifecycleStatusConflict");
      }
      if (data?.errorFieldsArray.length > 0) return false;
    }

    return true;
  };

  const saveData = async () => {
    setIsValidating(true);
    setFormDirty(false);
    
    delete data.EnableScoping;
    if (!isFieldsValid(menu)) {
      data.validateFields = true;
      dispatchData(data);
      setIsValidating(false);
    } else {
      const { data: { value } } = await api.getTicketTemplates(`?$filter=Name eq '${encodeURIComponent(data.Name.replace(/'/ig, "''"))}'&skipScoping=true`);

      const result = value || [];
      if (result?.some(x => x.Id != props.id)) {
        data.validateFields = true;
        data.errorFieldsArray.push('DupeName');
        dispatchData(data);
        setIsValidating(false);
      } else {
        setIsValidating(false);
        saveDataAsync();
      }
    }
  };

  const countInput = (items, cnt) => {
    for (let element of items) {
      if (element.type.includes("Input") || element.type == CardPeoplePicker.JsonTypeName || element.id?.startsWith("Ticket."))
        cnt++;
      if (element.type == "Container" || element.type == "Column") {
        if (element.items)
          cnt = countInput(element.items, cnt);
      }
      if (element.type == "ColumnSet") {
        if (element.columns)
          cnt = countInput(element.columns, cnt);
      }
    }
    return cnt;
  }

  const disableSave = () => {

    if (!data.IsDefault && !(data?.Name?.length > 0))
      return true;

    if (!data.IsDefault && data?.LifecycleId && data.Status &&
      (data.Status.Guid === STATUS_GUID.RESOLVED || data.Status.Guid === STATUS_GUID.CLOSED))
      return true;

    if (data.EnableScoping && data.ScopedMembers.length==0)
      return true;

    if (!data?.isCardValid)
      return true;

    if (data.NoCustomForm)
      return !isFormUpdated;

    if (data.CardResponseJson?.length < 1 && data.TemplateForms?.length < 1)
      return true;

    if (isFormLogicInvalid)
      return true;

    return !isFormUpdated;
  };

  const navs: Breadcrumbs = {
    breadcrumbs: [
      {
        title: toTitleCase(SETTINGS),
        link: `/settings`
      },
      {
        title: toTitleCase(TEMPLATES),
        link: `/settings/templates`
      },
      {
        title:
          data['Name'] && data['Name'].length > 0
            ? data['Name']
            : toTitleCase(NEW_TEMPLATE),
        link: ''
      }
    ]
  };

  const tabContent: Record<TemplateTabKey, { key: string; content: string; description: string }> = {
    fields: {
      key: 'fields',
      content: TEMPLATE.TEMPLATE_HEADER,
      description: ''
    },
    forms: {
      key: 'forms',
      content: TEMPLATE.TEMPLATE_FORM,
      description: TEMPLATE.CREATE_CUSTOM_FORM
    },
    sharing: {
      key: 'sharing',
      content: TEMPLATE.TEMPLATE_SHARING,
      description: ""
    }
  };

  const menuItems = buildTemplateTabKeys({
    isDefault: data.IsDefault,
    isCustomFormFeatureEnabled,
    isScopingEnabled
  }).map(key => tabContent[key]);

  const HeaderElement = (props: any) => {
    return (
      <div>
        <Flex styles={{ paddingTop: '1.75rem' }}>
          <Text content={props.title} weight="bold" size="large" />
        </Flex>
        {props.description && (
          <Flex styles={{ padding: '0.5rem 0' }}>
            <Text content={props.description} />
          </Flex>
        )}
      </div>
    );
  };

  const menuIndexChange = (e, props: any) => {
    if (!isFieldsValid(menu)) {
      data.validateFields = true;
      dispatchData(data);
    } else {
      setMenu(props.activeIndex);
    }
  };

  const renderMenu = () => {
    // The tab count varies with the licence and with IsDefault, so a ?menu= index from a
    // bookmark can point past the end of the list. Fall back to the first tab. #35337
    const selectedMenu = menuItems[menu] ? menu : 0;

    const menuItem = menuItems[selectedMenu];
    const headerContent = (
      <HeaderElement
        title={menuItem.content}
        description={menuItem.description}
      />
    );
    switch (menuItem.key) {
      case 'fields':
        return (
          <Step_DefineFields
            headerContent={headerContent}
            templateData={data}
            formData={templateProps}
            dispatchData={dispatchData}
          />
        );
      case 'forms':
        return (
          (!isMultiTurnCardFeatureEnabled && !data.IsDefault) ?
            <UpgradeNeeded
              headerText={t('ticket.templates-form.no-access')}
              subheaderText={t('ticket.templates-form.upgrade-plan')} /> :
            <TemplateForm
              setFormList={updateTemplateForms}
              formList={data.TemplateForms}
              templateData={data}
              dispatchData={dispatchData}
              isFeatureEnabled={isMultiTurnCardFeatureEnabled}
            />
        );
      case 'sharing':
        return (
          <Flex.Item size="size.half">
            <Flex column style={{ flex: 1 }} className={`overflow-auto`} gap="gap.medium">
              <Flex.Item>
                <div>
                  <Flex>
                    {headerContent && <>{headerContent}</>}
                    {!headerContent && (<Text content={TEMPLATE.TEMPLATE_HEADER} weight="bold" size="large" /> )}
                  </Flex>
                </div>
              </Flex.Item>
              <Flex.Item>
                <ScopePermissionPicker 
                  data={data}
                  changed={(members, isEnabled) => {
                    data.EnableScoping = isEnabled;
                    data.ScopedMembers = members;
                    dispatchData(data);
                  }} 
                />
              </Flex.Item>
            </Flex>
          </Flex.Item>
        );
      default:
        return <></>;
    }
  };

  useEffect(() => {
    setFormDirty(isFormUpdated);
  }, [isFormUpdated]);

  const updateTemplateForms = (templateForms: any) => {
    if (templateForms != undefined) {
      data.TemplateForms.push(templateForms)
      dispatchData(data);
    }
  };

  useEffect(() => {
    if (!isDataLoaded) return;

    //Add properties in data that are not in originalData
    Object.keys(data).forEach(key => {
      if (!(key in originalData)) {
        originalData[key] = undefined;
      }
    });

    setIsFormLogicInvalid(checkIfFormLogicInvalid());
    setIsFormUpdated(checkIfFormUpdated());
  }, [data, isDataLoaded])

  const ignoredProperties = new Set([
    "Id",
    "Guid",
    "CreatedDate",
    "CreatedById",
    "ModifiedDate",
    "ModifiedById",
    "IsDeleted",
    "DataSouceId",
    "IsExpand",
    "EnableScoping"
  ]);

  const checkIfFormUpdated = (): boolean => {
    const listTypes = ["TemplateForms", "Transitions", "TransitionGroups", "Filters"];
    const propTypes = ["Form"];

    if (haveScopedMembersChanged(data.ScopedMembers, originalData.ScopedMembers))
      return true;

    const { ScopedMembers: updatedScopedMembers, ...updatedRest } = data;
    const { ScopedMembers: initialScopedMembers, ...initialRest } = originalData;

    return compareObjectProperties(updatedRest, initialRest, listTypes, propTypes);
  }

  const checkIfFormLogicInvalid = (): boolean => {
    const objList: any[] = data["TemplateForms"] ?? [];
    const isInvalid = objList.some(o => o.Transitions?.some(g => g.UseLogicBuilder &&
      (g.TransitionGroups?.some(f => f.Filters?.some(x => !x.Property || (!x.Value && ![AutomationOperators.IsNull, AutomationOperators.IsNotNull].includes(x.Operator)))) ||
        (g.ActionType == "MoveToXForm" && (!g.TransitionFormIndex || g.TransitionFormIndex >= objList.length)))
    ));
    return isInvalid;
  }

  const compareObjectList = (updatedList: any[], initialList: any[], listTypes: string[], propTypes: string[]) => {
    if ((updatedList?.length ?? 0) !== (initialList?.length ?? 0)) return true;
    let isObjectUpdated = false;
    for (let i = 0; i < updatedList.length; i++) {
      isObjectUpdated = compareObjectProperties(updatedList[i], initialList[i], listTypes, propTypes);
      if (isObjectUpdated) break;
    }
    return isObjectUpdated;
  }

  const compareObjectProperties = (updatedObject: any, initialObject: any, listTypes: string[], propTypes: string[]): boolean => {
    let isUpdated = false;
    let propertiesList = Object.keys(updatedObject);
    for (let property of propertiesList) {

      if (ignoredProperties.has(property)) {
        continue;
      }
      if (listTypes.includes(property)) {
        isUpdated = compareObjectList(updatedObject[property], initialObject[property], listTypes, propTypes);
        if (isUpdated) break;
      }
      else if (propTypes.includes(property)) {
        isUpdated = compareObjectProperties(updatedObject[property], initialObject[property], listTypes, propTypes);
        if (isUpdated) break;
      }
      else if ((updatedObject[property]?.toString() ?? "") !== (initialObject[property]?.toString() ?? "")) {
        isUpdated = true;
        break;
      }
      
    }
    return isUpdated;
  }

  return (
    <CheckLogin onSignedIn={loadPage}>
      <Helmet>
        <title>{toTitleCase(TEMPLATES)} - Tikit</title>
      </Helmet>
      <div className={`hidden md:block ${ConfigCSS.configTemplateContainer}`}>
        {apiStatus.status === LOADING ? (
          <Loader label={apiStatus.msg} styles={{ height: '100vh' }} />
        ) : apiStatus.status === ERROR ? (
          <>
            <Communication {...errConfig} />
            {apiStatus.errCode === 401 && (
              <Button
                content={REFRESH_SESSION}
                primary
                onClick={e => {
                  Providers.globalProvider.login();
                }}
              />
            )}
          </>
        ) : (
          <ProviderConsumer
            render={globalTheme => (
              <>
                {(!isTemplateFeatureEnabled) ? <UpgradeNeeded
                  toolbarText={`${toTitleCase(SETTINGS)} > ${toTitleCase(TEMPLATES)}`}
                  headerText={t('ticket.templates.no-access')}
                  subheaderText={t('ticket.templates.upgrade-plan')} /> :
                  (<>
                    <Toolbar
                      onInteraction={onToolbarInteraction}
                      globalTheme={globalTheme}
                      title=""
                      breadcrumbs={navs}
                      saveText={SAVE}
                      saveCommand={saveData}
                      saveLoading={isValidating}
                      saveLoadingText={'Validating'}
                      disableSave={disableSave()}
                    />

                    <Flex className={ConfigCSS.configTemplateBody}>
                      <Flex.Item>
                        <Menu
                          activeIndex={menu}
                          items={menuItems}
                          vertical
                          pointing
                          onActiveIndexChange={menuIndexChange}
                          styles={({ theme: { siteVariables } }) => ({
                            height: '100%',
                            paddingTop: '1.5rem',
                            backgroundColor:
                              siteVariables.colorScheme.default.background2,
                            borderRight: `solid 1px ${siteVariables.colorScheme.default.border}`
                          })}
                        />
                      </Flex.Item>
                      <Flex.Item>
                        <Flex
                          fill
                          className={`pr-3`}
                          styles={({ theme: { siteVariables } }) => ({
                            backgroundColor:
                              siteVariables.colorScheme.default.background,
                            overflow: 'auto',
                            padding: '0 1.25rem'
                          })}
                        >
                          {renderMenu()}
                        </Flex>
                      </Flex.Item>
                    </Flex>
                  </>)}
              </>
            )}
          />
        )}
      </div>
      <MobileSettingsView />
      {Prompt}
      {Prompt}
    </CheckLogin>
  );
};
